import React, { useState, useEffect, useMemo } from 'react';
import { X, XCircle, CheckCircle2, Pencil } from 'lucide-react';

function formatTime(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Santiago',
    });
  } catch {
    return '—';
  }
}

function toTimeInputValue(isoString, plannedHHMM) {
  if (isoString) {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'America/Santiago',
      }).formatToParts(new Date(isoString));
      const h = parts.find((p) => p.type === 'hour')?.value ?? '00';
      const m = parts.find((p) => p.type === 'minute')?.value ?? '00';
      return `${h}:${m}`;
    } catch {
      /* fall through */
    }
  }
  if (plannedHHMM && /^\d{1,2}:\d{2}$/.test(plannedHHMM)) {
    const [h, m] = plannedHHMM.split(':');
    return `${h.padStart(2, '0')}:${m}`;
  }
  return '';
}

/**
 * Modal de validación / corrección de fichaje.
 * mode: 'approve' | 'reject' | 'correct'
 * onConfirm({ observations, clockIn, clockOut }) — clockIn/clockOut son ISO o null
 */
export function LaborAttendanceValidationModal({
  open,
  mode = 'reject',
  employeeName,
  date,
  plannedStart,
  plannedEnd,
  clockInRaw,
  clockOutRaw,
  onConfirm,
  onClose,
}) {
  const [clockInTime, setClockInTime] = useState('');
  const [clockOutTime, setClockOutTime] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isApprove = mode === 'approve' || mode === 'correct';
  const isCorrect = mode === 'correct';
  const requiresBothTimes = isCorrect;

  const defaultClockIn = useMemo(
    () => toTimeInputValue(clockInRaw, plannedStart),
    [clockInRaw, plannedStart]
  );
  const defaultClockOut = useMemo(
    () => toTimeInputValue(clockOutRaw, plannedEnd),
    [clockOutRaw, plannedEnd]
  );

  useEffect(() => {
    if (open) {
      setClockInTime(defaultClockIn);
      setClockOutTime(defaultClockOut);
      setNotes('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, mode, defaultClockIn, defaultClockOut]);

  if (!open) return null;

  const buildIsoFromTime = (timeStr) => {
    if (!timeStr || !date) return null;
    const d = new Date(`${date}T${timeStr}:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isApprove && !notes.trim()) {
      setError('La nota de rechazo es obligatoria.');
      return;
    }

    const clockIn = buildIsoFromTime(clockInTime);
    const clockOut = buildIsoFromTime(clockOutTime);

    if (isApprove) {
      if (requiresBothTimes && (!clockIn || !clockOut)) {
        setError('Indica entrada y salida para registrar la corrección.');
        return;
      }
      if (!clockInRaw && !clockOutRaw && (!clockIn || !clockOut)) {
        setError('Indica entrada y salida para validar sin fichaje del torniquete.');
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({
        observations: notes.trim(),
        clockIn: isApprove ? clockIn : null,
        clockOut: isApprove ? clockOut : null,
      });
    } catch (err) {
      setError(
        err.message ||
          (isApprove ? 'Error al validar el fichaje.' : 'Error al rechazar el fichaje.')
      );
      setSubmitting(false);
    }
  };

  const title = isCorrect
    ? 'Corregir y validar fichaje'
    : isApprove
      ? 'Confirmar validación'
      : 'Rechazar fichaje';

  const Icon = isApprove ? (isCorrect ? Pencil : CheckCircle2) : XCircle;
  const iconClass = isApprove ? 'text-emerald-400' : 'text-red-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 shrink-0 ${iconClass}`} />
            <h2 className="text-base font-semibold text-white">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {employeeName && (
            <p className="text-sm text-gray-400">
              Empleado: <span className="text-white font-medium">{employeeName}</span>
            </p>
          )}

          {plannedStart && plannedEnd && (
            <div className="rounded-lg bg-gray-800/60 border border-gray-700 px-3 py-2 text-xs text-gray-400">
              Planificado:{' '}
              <span className="font-mono text-gray-300">{plannedStart}</span>
              <span className="mx-1">→</span>
              <span className="font-mono text-gray-300">{plannedEnd}</span>
            </div>
          )}

          {(clockInRaw || clockOutRaw) && (
            <div className="rounded-lg bg-gray-800/80 border border-gray-700 px-3 py-2 text-sm">
              <p className="text-xs text-gray-500 mb-1">Fichaje raw (torniquete)</p>
              <p className="font-mono text-xs">
                <span className="text-emerald-400">{formatTime(clockInRaw)}</span>
                <span className="text-gray-600 mx-2">/</span>
                <span className="text-rose-400">{formatTime(clockOutRaw)}</span>
              </p>
            </div>
          )}

          {isApprove && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="att-clock-in" className="block text-xs font-medium text-gray-400 mb-1">
                  Entrada validada
                </label>
                <input
                  id="att-clock-in"
                  type="time"
                  value={clockInTime}
                  onChange={(e) => setClockInTime(e.target.value)}
                  disabled={submitting}
                  className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>
              <div>
                <label htmlFor="att-clock-out" className="block text-xs font-medium text-gray-400 mb-1">
                  Salida validada
                </label>
                <input
                  id="att-clock-out"
                  type="time"
                  value={clockOutTime}
                  onChange={(e) => setClockOutTime(e.target.value)}
                  disabled={submitting}
                  className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>
            </div>
          )}

          {isApprove && isCorrect && (
            <p className="text-xs text-gray-500">
              Ajusta las horas que faltan o corrige un fichaje erróneo antes de validar.
            </p>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              {isApprove ? 'Observaciones' : 'Motivo del rechazo'}
              {!isApprove && <span className="text-red-400"> *</span>}
              {isApprove && <span className="text-gray-500 font-normal"> (opcional)</span>}
            </label>
            <textarea
              className={`w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 ${
                isApprove ? 'focus:ring-emerald-500/50' : 'focus:ring-red-500/50'
              }`}
              rows={3}
              placeholder={
                isApprove
                  ? 'Añade una observación si es necesario…'
                  : 'Explica por qué se rechaza este fichaje…'
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              autoFocus={!isApprove}
            />
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors disabled:opacity-50 ${
                isApprove ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-red-600 hover:bg-red-500'
              }`}
            >
              {submitting
                ? isApprove
                  ? 'Validando…'
                  : 'Rechazando…'
                : isCorrect
                  ? 'Corregir y validar'
                  : isApprove
                    ? 'Confirmar validación'
                    : 'Confirmar rechazo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
